import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, Save, Plus, Trash2, GripVertical, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconOptions = [
  { value: 'wallet', label: 'Wallet (COD)' },
  { value: 'truck', label: 'Truck (Delivery)' },
  { value: 'refresh', label: 'Refresh (Returns)' },
  { value: 'shield', label: 'Shield (Security)' },
  { value: 'check', label: 'Check' },
  { value: 'star', label: 'Star' },
  { value: 'gift', label: 'Gift' },
  { value: 'heart', label: 'Heart' }
];

const defaultSettings = {
  enabled: true,
  background_color: '#FEF3C7',
  text_color: '#92400E',
  items: [
    { text: 'Cash on Delivery Available', icon: 'wallet', enabled: true },
    { text: 'Free Home Delivery', icon: 'truck', enabled: true },
    { text: 'Easy Returns', icon: 'refresh', enabled: true },
    { text: 'Secure Payments', icon: 'shield', enabled: true }
  ],
  animation_speed: 30
};

const AdminAnnouncement = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcement-bar`);
      setSettings({ ...defaultSettings, ...response.data });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/announcement-bar`, settings);
      toast.success('Announcement bar updated successfully!');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update announcement bar');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setSettings(prev => ({
      ...prev,
      items: [...prev.items, { text: 'New Message', icon: 'check', enabled: true }]
    }));
  };

  const removeItem = (index) => {
    setSettings(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const toggleItemEnabled = (index) => {
    setSettings(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, enabled: !item.enabled } : item)
    }));
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="announcement" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]" data-testid="announcement-title">Announcement Bar</h1>
          </div>
          <Button data-testid="logout-button" onClick={handleLogout} variant="ghost" className="text-zinc-600">
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>

        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-[#0f172a]">Announcement Bar Settings</h2>
                <p className="text-sm text-zinc-500">Configure the scrolling announcement bar displayed below the header</p>
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="bar-enabled" className="text-sm">Bar Enabled</Label>
                <Switch
                  id="bar-enabled"
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label htmlFor="bg-color">Background Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    id="bg-color"
                    value={settings.background_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={settings.background_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="text-color">Text Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    id="text-color"
                    value={settings.text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={settings.text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="animation-speed">Animation Speed (seconds)</Label>
                <Input
                  type="number"
                  id="animation-speed"
                  value={settings.animation_speed}
                  onChange={(e) => setSettings(prev => ({ ...prev, animation_speed: parseInt(e.target.value) || 30 }))}
                  className="mt-1"
                  min="10"
                  max="120"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="mb-6">
              <Label className="mb-2 block">Preview</Label>
              <div 
                className="overflow-hidden rounded-lg"
                style={{ backgroundColor: settings.background_color }}
              >
                <div className="flex items-center whitespace-nowrap py-2 px-4" style={{ color: settings.text_color }}>
                  {settings.items.filter(i => i.enabled).map((item, idx) => (
                    <span key={idx} className="inline-flex items-center px-4">
                      <span className="text-sm font-medium">{item.text}</span>
                      <span className="mx-4 opacity-40">•</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-[#0f172a]">Announcement Messages</h2>
                <p className="text-sm text-zinc-500">Add, edit, or remove announcement messages</p>
              </div>
              <Button onClick={addItem} size="sm" variant="outline" data-testid="add-announcement">
                <Plus className="w-4 h-4 mr-1" /> Add Message
              </Button>
            </div>

            <div className="space-y-3">
              {settings.items.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-4 rounded-lg border ${item.enabled ? 'bg-white border-zinc-200' : 'bg-zinc-50 border-zinc-100'}`}
                  data-testid={`announcement-item-${index}`}
                >
                  <GripVertical className="w-5 h-5 text-zinc-400 cursor-grab" />
                  
                  <select
                    value={item.icon}
                    onChange={(e) => updateItem(index, 'icon', e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {iconOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  <Input
                    value={item.text}
                    onChange={(e) => updateItem(index, 'text', e.target.value)}
                    className="flex-1"
                    placeholder="Announcement message..."
                  />

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleItemEnabled(index)}
                    className={item.enabled ? 'text-green-600' : 'text-zinc-400'}
                  >
                    {item.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={settings.items.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t">
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-[#064E3B] hover:bg-[#065F46]"
                data-testid="save-announcement"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncement;
